/**
 * Tooltip Registry
 *
 * Centralized registry of all tooltip metadata.
 * Actual text content is in i18n JSON files.
 *
 * Usage:
 *   import { getTooltipMeta, TOOLTIP_IDS } from './registry';
 *   const meta = getTooltipMeta('settings.organization.region');
 */

import type { TooltipMeta, PageHelpEntry, MetricHelpEntry, PageId } from './schema';

/**
 * Type-safe tooltip IDs.
 * Add new IDs here to get autocomplete in components.
 */
export const TOOLTIP_IDS = {
  // Organization Settings
  'settings.organization.name': 'settings.organization.name',
  'settings.organization.region': 'settings.organization.region',
  'settings.organization.timezone': 'settings.organization.timezone',
  'settings.organization.industry': 'settings.organization.industry',
  'settings.organization.description': 'settings.organization.description',
  'settings.organization.language': 'settings.organization.language',

  // AI Settings
  'settings.ai.asrProvider': 'settings.ai.asrProvider',
  'settings.ai.asrModel': 'settings.ai.asrModel',
  'settings.ai.llmProvider': 'settings.ai.llmProvider',
  'settings.ai.llmModel': 'settings.ai.llmModel',
  'settings.ai.autoAnalysis': 'settings.ai.autoAnalysis',
  'settings.ai.sentimentAnalysis': 'settings.ai.sentimentAnalysis',
  'settings.ai.keyMoments': 'settings.ai.keyMoments',

  // Quality Settings
  'settings.quality.scoreThreshold': 'settings.quality.scoreThreshold',
  'settings.quality.minScore': 'settings.quality.minScore',

  // Project Settings - Overview
  'settings.project.name': 'settings.project.name',
  'settings.project.description': 'settings.project.description',
  'settings.project.channels': 'settings.project.channels',
  'settings.project.defaultProject': 'settings.project.defaultProject',

  // Project Settings - AI Configuration
  'settings.project.companyContext': 'settings.project.companyContext',
  'settings.project.dataRetention': 'settings.project.dataRetention',

  // Project Settings - Quality & Alerts
  'settings.project.promptProfile': 'settings.project.promptProfile',
  'settings.project.scriptAdherence': 'settings.project.scriptAdherence',
  'settings.project.qualityThreshold': 'settings.project.qualityThreshold',
  'settings.project.complianceThreshold': 'settings.project.complianceThreshold',

  // Routing
  'settings.routing.condition': 'settings.routing.condition',
  'settings.routing.priority': 'settings.routing.priority',

  // CRM Integration
  'settings.crm.fieldMapping': 'settings.crm.fieldMapping',
  'settings.crm.testDelivery': 'settings.crm.testDelivery',

  // Data Sources
  'settings.datasources.apiKeys': 'settings.datasources.apiKeys',

  // Scripts
  'scripts.weight': 'scripts.weight',
  'scripts.compliance': 'scripts.compliance',
  'scripts.name': 'scripts.name',
  'scripts.description': 'scripts.description',
  'scripts.category': 'scripts.category',
  'scripts.active': 'scripts.active',
  'scripts.steps': 'scripts.steps',
  'scripts.stepRequired': 'scripts.stepRequired',
  'scripts.orderEnforced': 'scripts.orderEnforced',
  'scripts.phrases': 'scripts.phrases',
  'scripts.phraseType': 'scripts.phraseType',
  'scripts.matchType': 'scripts.matchType',
  'scripts.semanticThreshold': 'scripts.semanticThreshold',

  // Notifications
  'settings.notifications.email': 'settings.notifications.email',
  'settings.notifications.businessAlerts': 'settings.notifications.businessAlerts',
  'settings.notifications.systemAlerts': 'settings.notifications.systemAlerts',
  'settings.notifications.weeklyReports': 'settings.notifications.weeklyReports',

  // Billing
  'settings.billing.budget': 'settings.billing.budget',
  'settings.billing.paymentMethod': 'settings.billing.paymentMethod',
  'settings.billing.autoRecharge': 'settings.billing.autoRecharge',

  // Data Sources
  'settings.datasources.projects': 'settings.datasources.projects',
  'settings.datasources.bitrix24': 'settings.datasources.bitrix24',

  // Team
  'settings.team.members': 'settings.team.members',
  'settings.team.invites': 'settings.team.invites',

  // Routing (additional)
  'settings.routing.rules': 'settings.routing.rules',

  // CRM (additional)
  'settings.crm.provider': 'settings.crm.provider',
  'settings.crm.dataLoader': 'settings.crm.dataLoader',
} as const;

export type TooltipId = (typeof TOOLTIP_IDS)[keyof typeof TOOLTIP_IDS];

/**
 * Tooltip metadata registry.
 * Priority fields for Phase 1 marked with ⭐
 */
const TOOLTIP_REGISTRY: Record<TooltipId, TooltipMeta> = {
  // ========================================
  // Organization Settings
  // ========================================

  'settings.organization.name': {
    id: 'settings.organization.name',
    category: 'settings.organization',
    complexity: 'low',
    roles: ['admin', 'owner'],
  },

  // Informational only: single EU deployment, no region choice exists
  'settings.organization.region': {
    id: 'settings.organization.region',
    category: 'settings.organization',
    complexity: 'medium',
    roles: ['admin', 'owner'],
    onboarding: {
      step: 1,
      tour: 'admin',
      highlight: true,
    },
    learnMorePath: 'user-guide/settings',
  },

  'settings.organization.timezone': {
    id: 'settings.organization.timezone',
    category: 'settings.organization',
    complexity: 'low',
    roles: ['admin', 'owner'],
  },

  'settings.organization.industry': {
    id: 'settings.organization.industry',
    category: 'settings.organization',
    complexity: 'low',
    roles: ['admin', 'owner'],
  },

  'settings.organization.description': {
    id: 'settings.organization.description',
    category: 'settings.organization',
    complexity: 'high',
    roles: ['admin', 'owner'],
    isRich: true,
    onboarding: {
      step: 2,
      tour: 'admin',
      highlight: true,
    },
  },

  'settings.organization.language': {
    id: 'settings.organization.language',
    category: 'settings.organization',
    complexity: 'low',
    roles: ['admin', 'owner'],
  },

  // ========================================
  // AI Settings
  // ========================================

  // ⭐ PRIORITY: ASR provider selection is complex
  'settings.ai.asrProvider': {
    id: 'settings.ai.asrProvider',
    category: 'providers.asr',
    complexity: 'high',
    roles: ['admin', 'owner'],
    isRich: true,
    learnMorePath: 'user-guide/settings',
  },

  'settings.ai.asrModel': {
    id: 'settings.ai.asrModel',
    category: 'providers.asr',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  // ⭐ PRIORITY: LLM provider selection
  'settings.ai.llmProvider': {
    id: 'settings.ai.llmProvider',
    category: 'providers.llm',
    complexity: 'high',
    roles: ['admin', 'owner'],
    isRich: true,
    learnMorePath: 'user-guide/settings',
  },

  'settings.ai.llmModel': {
    id: 'settings.ai.llmModel',
    category: 'providers.llm',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  'settings.ai.autoAnalysis': {
    id: 'settings.ai.autoAnalysis',
    category: 'settings.ai',
    complexity: 'low',
    roles: ['admin', 'owner'],
  },

  'settings.ai.sentimentAnalysis': {
    id: 'settings.ai.sentimentAnalysis',
    category: 'settings.ai',
    complexity: 'low',
    roles: ['admin', 'owner'],
  },

  'settings.ai.keyMoments': {
    id: 'settings.ai.keyMoments',
    category: 'settings.ai',
    complexity: 'low',
    roles: ['admin', 'owner'],
  },

  // ========================================
  // Quality Settings
  // ========================================

  'settings.quality.scoreThreshold': {
    id: 'settings.quality.scoreThreshold',
    category: 'settings.alerts',
    complexity: 'high',
    roles: ['admin', 'owner'],
    isRich: true,
  },

  'settings.quality.minScore': {
    id: 'settings.quality.minScore',
    category: 'settings.alerts',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  // ========================================
  // Project Settings - Overview
  // ========================================

  'settings.project.name': {
    id: 'settings.project.name',
    category: 'settings.project',
    complexity: 'low',
    roles: [],
  },

  'settings.project.description': {
    id: 'settings.project.description',
    category: 'settings.project',
    complexity: 'low',
    roles: [],
  },

  'settings.project.channels': {
    id: 'settings.project.channels',
    category: 'settings.project',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  'settings.project.defaultProject': {
    id: 'settings.project.defaultProject',
    category: 'settings.project',
    complexity: 'low',
    roles: ['admin', 'owner'],
  },

  // ========================================
  // Project Settings - AI Configuration
  // ========================================

  // Deprecated: Company context moved to organization level
  'settings.project.companyContext': {
    id: 'settings.project.companyContext',
    category: 'settings.organization',
    complexity: 'high',
    roles: ['admin', 'owner'],
    isRich: true,
  },

  'settings.project.dataRetention': {
    id: 'settings.project.dataRetention',
    category: 'settings.project',
    complexity: 'medium',
    roles: ['admin', 'owner'],
    learnMorePath: 'user-guide/settings',
  },

  // ========================================
  // Project Settings - Quality & Alerts
  // ========================================

  // ⭐ PRIORITY: Prompt profile affects analysis quality
  'settings.project.promptProfile': {
    id: 'settings.project.promptProfile',
    category: 'settings.alerts',
    complexity: 'high',
    roles: ['admin', 'owner'],
    isRich: true,
  },

  'settings.project.scriptAdherence': {
    id: 'settings.project.scriptAdherence',
    category: 'settings.alerts',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  // ⭐ PRIORITY: Quality threshold is confusing
  'settings.project.qualityThreshold': {
    id: 'settings.project.qualityThreshold',
    category: 'settings.alerts',
    complexity: 'high',
    roles: ['admin', 'owner'],
    isRich: true,
    onboarding: {
      step: 2,
      tour: 'admin',
    },
  },

  'settings.project.complianceThreshold': {
    id: 'settings.project.complianceThreshold',
    category: 'settings.alerts',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  // ========================================
  // Routing
  // ========================================

  // ⭐ PRIORITY: Routing DSL syntax is complex
  'settings.routing.condition': {
    id: 'settings.routing.condition',
    category: 'settings.routing',
    complexity: 'high',
    roles: ['admin', 'owner'],
    isRich: true,
  },

  'settings.routing.priority': {
    id: 'settings.routing.priority',
    category: 'settings.routing',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  // ========================================
  // CRM Integration
  // ========================================

  // ⭐ PRIORITY: Field mapping is technical
  'settings.crm.fieldMapping': {
    id: 'settings.crm.fieldMapping',
    category: 'settings.crm',
    complexity: 'high',
    roles: ['admin', 'owner'],
    isRich: true,
  },

  'settings.crm.testDelivery': {
    id: 'settings.crm.testDelivery',
    category: 'settings.crm',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  // ========================================
  // Data Sources
  // ========================================

  // ⭐ PRIORITY: API keys security
  'settings.datasources.apiKeys': {
    id: 'settings.datasources.apiKeys',
    category: 'settings.organization',
    complexity: 'high',
    roles: ['owner'],
    isRich: true,
    learnMorePath: 'user-guide/data-sources',
  },

  // ========================================
  // Scripts
  // ========================================

  'scripts.weight': {
    id: 'scripts.weight',
    category: 'scripts',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  'scripts.compliance': {
    id: 'scripts.compliance',
    category: 'scripts',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  'scripts.name': {
    id: 'scripts.name',
    category: 'scripts',
    complexity: 'low',
    roles: [],
  },

  'scripts.description': {
    id: 'scripts.description',
    category: 'scripts',
    complexity: 'low',
    roles: [],
  },

  'scripts.category': {
    id: 'scripts.category',
    category: 'scripts',
    complexity: 'low',
    roles: [],
  },

  'scripts.active': {
    id: 'scripts.active',
    category: 'scripts',
    complexity: 'low',
    roles: ['admin', 'owner'],
  },

  'scripts.steps': {
    id: 'scripts.steps',
    category: 'scripts',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  'scripts.stepRequired': {
    id: 'scripts.stepRequired',
    category: 'scripts',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  'scripts.orderEnforced': {
    id: 'scripts.orderEnforced',
    category: 'scripts',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  'scripts.phrases': {
    id: 'scripts.phrases',
    category: 'scripts',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  'scripts.phraseType': {
    id: 'scripts.phraseType',
    category: 'scripts',
    complexity: 'high',
    roles: ['admin', 'owner'],
    isRich: true,
  },

  'scripts.matchType': {
    id: 'scripts.matchType',
    category: 'scripts',
    complexity: 'high',
    roles: ['admin', 'owner'],
    isRich: true,
  },

  'scripts.semanticThreshold': {
    id: 'scripts.semanticThreshold',
    category: 'scripts',
    complexity: 'high',
    roles: ['admin', 'owner'],
  },

  // ========================================
  // Notifications
  // ========================================

  'settings.notifications.email': {
    id: 'settings.notifications.email',
    category: 'settings.notifications',
    complexity: 'low',
    roles: [],
  },

  'settings.notifications.businessAlerts': {
    id: 'settings.notifications.businessAlerts',
    category: 'settings.notifications',
    complexity: 'medium',
    roles: ['admin', 'owner', 'supervisor'],
  },

  'settings.notifications.systemAlerts': {
    id: 'settings.notifications.systemAlerts',
    category: 'settings.notifications',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  'settings.notifications.weeklyReports': {
    id: 'settings.notifications.weeklyReports',
    category: 'settings.notifications',
    complexity: 'low',
    roles: [],
  },

  // ========================================
  // Billing
  // ========================================

  'settings.billing.budget': {
    id: 'settings.billing.budget',
    category: 'settings.billing',
    complexity: 'medium',
    roles: ['owner'],
  },

  'settings.billing.paymentMethod': {
    id: 'settings.billing.paymentMethod',
    category: 'settings.billing',
    complexity: 'low',
    roles: ['owner'],
  },

  'settings.billing.autoRecharge': {
    id: 'settings.billing.autoRecharge',
    category: 'settings.billing',
    complexity: 'medium',
    roles: ['owner'],
  },

  // ========================================
  // Data Sources (additional)
  // ========================================

  'settings.datasources.projects': {
    id: 'settings.datasources.projects',
    category: 'settings.project',
    complexity: 'low',
    roles: [],
  },

  'settings.datasources.bitrix24': {
    id: 'settings.datasources.bitrix24',
    category: 'settings.crm',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  // ========================================
  // Team
  // ========================================

  'settings.team.members': {
    id: 'settings.team.members',
    category: 'settings.team',
    complexity: 'low',
    roles: ['admin', 'owner'],
  },

  'settings.team.invites': {
    id: 'settings.team.invites',
    category: 'settings.team',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  // ========================================
  // Routing (additional)
  // ========================================

  'settings.routing.rules': {
    id: 'settings.routing.rules',
    category: 'settings.routing',
    complexity: 'high',
    roles: ['admin', 'owner'],
    isRich: true,
  },

  // ========================================
  // CRM (additional)
  // ========================================

  'settings.crm.provider': {
    id: 'settings.crm.provider',
    category: 'settings.crm',
    complexity: 'medium',
    roles: ['admin', 'owner'],
  },

  'settings.crm.dataLoader': {
    id: 'settings.crm.dataLoader',
    category: 'settings.crm',
    complexity: 'high',
    roles: ['admin', 'owner'],
  },
};

/**
 * Get tooltip metadata by ID
 */
export function getTooltipMeta(id: TooltipId): TooltipMeta | undefined {
  return TOOLTIP_REGISTRY[id];
}

/**
 * Get all tooltips by category
 */
export function getTooltipsByCategory(category: TooltipMeta['category']): TooltipMeta[] {
  return Object.values(TOOLTIP_REGISTRY).filter((meta) => meta.category === category);
}

/**
 * Get tooltips for onboarding tour
 */
export function getOnboardingTooltips(
  tour: 'admin' | 'agent' | 'supervisor'
): TooltipMeta[] {
  return Object.values(TOOLTIP_REGISTRY)
    .filter((meta) => meta.onboarding?.tour === tour)
    .sort((a, b) => (a.onboarding?.step ?? 0) - (b.onboarding?.step ?? 0));
}

/**
 * Check if tooltip should be shown to user role
 */
export function isTooltipVisibleForRole(
  id: TooltipId,
  userRole: TooltipMeta['roles'][number]
): boolean {
  const meta = getTooltipMeta(id);
  if (!meta) return false;

  // Empty roles array means visible to all
  if (meta.roles.length === 0) return true;

  return meta.roles.includes(userRole);
}

/**
 * Get all tooltip IDs (for export/documentation)
 */
export function getAllTooltipIds(): TooltipId[] {
  return Object.keys(TOOLTIP_REGISTRY) as TooltipId[];
}

/**
 * Count tooltips by complexity
 */
export function getTooltipStats(): Record<TooltipMeta['complexity'], number> {
  const stats = { low: 0, medium: 0, high: 0 };
  Object.values(TOOLTIP_REGISTRY).forEach((meta) => {
    stats[meta.complexity]++;
  });
  return stats;
}

// ============================================================
// Page Help Registry (NEW)
// ============================================================

export const PAGE_HELP_REGISTRY: Record<PageId, PageHelpEntry> = {
  dashboard: { type: 'page', id: 'dashboard', i18nKey: 'pages.dashboard' },
  records: { type: 'page', id: 'records', i18nKey: 'pages.records' },
  trends: { type: 'page', id: 'trends', i18nKey: 'pages.trends' },
  agents: { type: 'page', id: 'agents', i18nKey: 'pages.agents' },
  alerts: { type: 'page', id: 'alerts', i18nKey: 'pages.alerts' },
  // Dashboard sub-tab pages
  conversion: { type: 'page', id: 'conversion', i18nKey: 'pages.conversion' },
  questions: { type: 'page', id: 'questions', i18nKey: 'pages.questions' },
  aiFeedback: { type: 'page', id: 'aiFeedback', i18nKey: 'pages.aiFeedback' },
  processing: { type: 'page', id: 'processing', i18nKey: 'pages.processing' },
  errors: { type: 'page', id: 'errors', i18nKey: 'pages.errors' },
  quality: { type: 'page', id: 'quality', i18nKey: 'pages.quality' },
  coaching: { type: 'page', id: 'coaching', i18nKey: 'pages.coaching' },
  agentPerformance: {
    type: 'page',
    id: 'agentPerformance',
    i18nKey: 'pages.agentPerformance',
  },
  agentProgress: { type: 'page', id: 'agentProgress', i18nKey: 'pages.agentProgress' },
  projects: { type: 'page', id: 'projects', i18nKey: 'pages.projects' },
  budget: { type: 'page', id: 'budget', i18nKey: 'pages.budget' },
  // Role-specific overview tabs
  ownerOverview: { type: 'page', id: 'ownerOverview', i18nKey: 'pages.ownerOverview' },
  adminOverview: { type: 'page', id: 'adminOverview', i18nKey: 'pages.adminOverview' },
  supervisorOverview: {
    type: 'page',
    id: 'supervisorOverview',
    i18nKey: 'pages.supervisorOverview',
  },
  highlights: { type: 'page', id: 'highlights', i18nKey: 'pages.highlights' },
  criteriaReport: {
    type: 'page',
    id: 'criteriaReport',
    i18nKey: 'pages.criteriaReport',
  },
  clientOverview: { type: 'page', id: 'clientOverview', i18nKey: 'pages.clientOverview' },
  voiceOfCustomer: {
    type: 'page',
    id: 'voiceOfCustomer',
    i18nKey: 'pages.voiceOfCustomer',
  },
};

/**
 * Get page help entry by page ID
 */
export function getPageHelpEntry(pageId: PageId): PageHelpEntry {
  return PAGE_HELP_REGISTRY[pageId];
}

// ============================================================
// Metric Registry (NEW)
// ============================================================

export const METRIC_IDS = {
  avgScore: 'avgScore',
  callsCount: 'callsCount',
  callsAnalyzed: 'callsAnalyzed',
  agentScore: 'agentScore',
  alertsCount: 'alertsCount',
  agentsCount: 'agentsCount',
  activeAgents: 'activeAgents',
  // Admin
  systemHealth: 'systemHealth',
  activeUsers: 'activeUsers',
  processingQueue: 'processingQueue',
  errorRate: 'errorRate',
  // Supervisor
  slaCompliance: 'slaCompliance',
  missedCalls: 'missedCalls',
  // Agent
  trendScore: 'trendScore',
  teamRank: 'teamRank',
  coaching: 'coaching',
  // Client
  saleProbability: 'saleProbability',
  costPerAnalysis: 'costPerAnalysis',
  // Owner
  revenuePipeline: 'revenuePipeline',
  budget: 'budget',
  orgsCount: 'orgsCount',
} as const;

export type MetricId = (typeof METRIC_IDS)[keyof typeof METRIC_IDS];

// Anchors (WS7 gate F-3) mirror docs/analytics/DASHBOARDS_WIDGET_INVENTORY.md:
// score-class metrics are `llm-opinion`, raw counts/revenue/health are
// `external`, and trends/ranks/ratios are `derived`.
const METRIC_REGISTRY: Record<MetricId, MetricHelpEntry> = {
  avgScore: {
    type: 'metric',
    id: 'avgScore',
    i18nKey: 'metrics.avgScore',
    anchor: 'llm-opinion',
  },
  callsCount: {
    type: 'metric',
    id: 'callsCount',
    i18nKey: 'metrics.callsCount',
    anchor: 'external',
  },
  callsAnalyzed: {
    type: 'metric',
    id: 'callsAnalyzed',
    i18nKey: 'metrics.callsAnalyzed',
    anchor: 'external',
  },
  agentScore: {
    type: 'metric',
    id: 'agentScore',
    i18nKey: 'metrics.agentScore',
    anchor: 'llm-opinion',
  },
  alertsCount: {
    type: 'metric',
    id: 'alertsCount',
    i18nKey: 'metrics.alertsCount',
    anchor: 'derived',
  },
  agentsCount: {
    type: 'metric',
    id: 'agentsCount',
    i18nKey: 'metrics.agentsCount',
    anchor: 'external',
  },
  activeAgents: {
    type: 'metric',
    id: 'activeAgents',
    i18nKey: 'metrics.activeAgents',
    anchor: 'external',
  },
  systemHealth: {
    type: 'metric',
    id: 'systemHealth',
    i18nKey: 'metrics.systemHealth',
    anchor: 'external',
  },
  activeUsers: {
    type: 'metric',
    id: 'activeUsers',
    i18nKey: 'metrics.activeUsers',
    anchor: 'external',
  },
  processingQueue: {
    type: 'metric',
    id: 'processingQueue',
    i18nKey: 'metrics.processingQueue',
    anchor: 'external',
  },
  errorRate: {
    type: 'metric',
    id: 'errorRate',
    i18nKey: 'metrics.errorRate',
    anchor: 'external',
  },
  slaCompliance: {
    type: 'metric',
    id: 'slaCompliance',
    i18nKey: 'metrics.slaCompliance',
    anchor: 'derived',
  },
  missedCalls: {
    type: 'metric',
    id: 'missedCalls',
    i18nKey: 'metrics.missedCalls',
    anchor: 'external',
  },
  trendScore: {
    type: 'metric',
    id: 'trendScore',
    i18nKey: 'metrics.trendScore',
    anchor: 'derived',
  },
  teamRank: {
    type: 'metric',
    id: 'teamRank',
    i18nKey: 'metrics.teamRank',
    anchor: 'derived',
  },
  coaching: {
    type: 'metric',
    id: 'coaching',
    i18nKey: 'metrics.coaching',
    anchor: 'llm-opinion',
  },
  saleProbability: {
    type: 'metric',
    id: 'saleProbability',
    i18nKey: 'metrics.saleProbability',
    anchor: 'llm-opinion',
  },
  costPerAnalysis: {
    type: 'metric',
    id: 'costPerAnalysis',
    i18nKey: 'metrics.costPerAnalysis',
    anchor: 'derived',
  },
  revenuePipeline: {
    type: 'metric',
    id: 'revenuePipeline',
    i18nKey: 'metrics.revenuePipeline',
    anchor: 'external',
  },
  budget: {
    type: 'metric',
    id: 'budget',
    i18nKey: 'metrics.budget',
    anchor: 'external',
  },
  orgsCount: {
    type: 'metric',
    id: 'orgsCount',
    i18nKey: 'metrics.orgsCount',
    anchor: 'external',
  },
};

/**
 * Get metric help entry by metric ID
 */
export function getMetricHelpEntry(metricId: MetricId): MetricHelpEntry {
  return METRIC_REGISTRY[metricId];
}
